/**
 * What a document is, said one way across heroes (owner, 20 Oct 2026 — D2:
 * a document kept once is used everywhere, so each hero must know what it
 * is). A Vault file carries the type it was confirmed as — the TelePortal
 * task it was captured for (`passport`, `employment_contract`…), the InOut
 * evidence item it was attached as (`boarding_pass`, `ticket`…), the identity
 * document read from it (`passport`, `id_card`) — and this map says what each
 * type is for the other hero:
 *
 *   • a TelePortal document, offered to a trip, as the InOut evidence item it
 *     can prove (an employment contract is the trip's "contract", a birth
 *     certificate an "official document");
 *   • an InOut item as itself — the trip's own kinds are document types too.
 *
 * A type with no InOut counterpart (a bank statement, an insurance policy) is
 * offered to a trip all the same, only never suggested: what it proves there
 * is the person's to say. The country's list decides which items exist — a
 * suggestion is only ever one of the items it offers.
 */

/**
 * Every type a Vault file can be confirmed as: TelePortal's (the kit's
 * catalogue — `teleportal.docType.*`), InOut's evidence items, and the identity
 * documents read from a file. One label each, under `teleportal.docType` — the
 * catalogue TelePortal's placeholders already name their documents by.
 */
export const DOCUMENT_TYPES = [
  // TelePortal — the kit's catalogue
  'bank_statements',
  'birth_certificate',
  'certified_translation',
  'consulate_invitation_letter',
  'criminal_record',
  'drivers_license',
  'employment_contract',
  'health_insurance_policy',
  'lease_agreement',
  'marriage_certificate',
  'passport',
  'pet_passport',
  'promise_contract',
  'proof_of_address',
  'proof_of_income',
  'salary_proof',
  'school_records',
  'sponsor_letter',
  'techvisa_endorsement',
  'term_of_responsibility',
  'travel_insurance',
  // InOut — the evidence items a trip is proved or explained by
  'boarding_pass',
  'passport_stamp',
  'ticket',
  'accommodation',
  'employer_declaration',
  'contract',
  'medical_documentation',
  'official_document',
  // Identity documents — read from a file (passport above)
  'id_card',
  // TelePortal — what a person captures beyond the kit (a task's "other documents")
  'visa',
  'work_permit',
  'residence_permit',
  'tax_document',
  'insurance',
  'certificate',
  'photo',
  'other',
] as const;

export type DocumentType = (typeof DOCUMENT_TYPES)[number];

/** Each type's label — literal keys, so the key-usage gate reads every one. */
export const DOCUMENT_TYPE_LABEL_KEY = {
  bank_statements: 'teleportal.docType.bank_statements',
  birth_certificate: 'teleportal.docType.birth_certificate',
  certified_translation: 'teleportal.docType.certified_translation',
  consulate_invitation_letter: 'teleportal.docType.consulate_invitation_letter',
  criminal_record: 'teleportal.docType.criminal_record',
  drivers_license: 'teleportal.docType.drivers_license',
  employment_contract: 'teleportal.docType.employment_contract',
  health_insurance_policy: 'teleportal.docType.health_insurance_policy',
  lease_agreement: 'teleportal.docType.lease_agreement',
  marriage_certificate: 'teleportal.docType.marriage_certificate',
  passport: 'teleportal.docType.passport',
  pet_passport: 'teleportal.docType.pet_passport',
  promise_contract: 'teleportal.docType.promise_contract',
  proof_of_address: 'teleportal.docType.proof_of_address',
  proof_of_income: 'teleportal.docType.proof_of_income',
  salary_proof: 'teleportal.docType.salary_proof',
  school_records: 'teleportal.docType.school_records',
  sponsor_letter: 'teleportal.docType.sponsor_letter',
  techvisa_endorsement: 'teleportal.docType.techvisa_endorsement',
  term_of_responsibility: 'teleportal.docType.term_of_responsibility',
  travel_insurance: 'teleportal.docType.travel_insurance',
  boarding_pass: 'teleportal.docType.boarding_pass',
  passport_stamp: 'teleportal.docType.passport_stamp',
  ticket: 'teleportal.docType.ticket',
  accommodation: 'teleportal.docType.accommodation',
  employer_declaration: 'teleportal.docType.employer_declaration',
  contract: 'teleportal.docType.contract',
  medical_documentation: 'teleportal.docType.medical_documentation',
  official_document: 'teleportal.docType.official_document',
  id_card: 'teleportal.docType.id_card',
  visa: 'teleportal.docType.visa',
  work_permit: 'teleportal.docType.work_permit',
  residence_permit: 'teleportal.docType.residence_permit',
  tax_document: 'teleportal.docType.tax_document',
  insurance: 'teleportal.docType.insurance',
  certificate: 'teleportal.docType.certificate',
  photo: 'teleportal.docType.photo',
  other: 'teleportal.docType.other',
} as const satisfies Record<DocumentType, string>;

/**
 * What a person may say a document is when they capture one for TelePortal
 * (D3): every type but InOut's trip evidence — a move's documents, the
 * identity card, and "other" for what none of them names.
 */
export const TELEPORTAL_CAPTURE_DOCUMENT_TYPES: readonly DocumentType[] = DOCUMENT_TYPES.filter(
  (type) =>
    ![
      'boarding_pass',
      'passport_stamp',
      'ticket',
      'accommodation',
      'employer_declaration',
      'medical_documentation',
      'official_document',
    ].includes(type)
);

/** What the classifier may name — every type but "other", which is no type at all. */
export const CLASSIFIABLE_DOCUMENT_TYPES: readonly DocumentType[] = DOCUMENT_TYPES.filter(
  (type) => type !== 'other'
);

export function isDocumentType(value: string): value is DocumentType {
  return (DOCUMENT_TYPES as readonly string[]).includes(value);
}

/** The label key of a file's type — null when the type is not known or not one of ours. */
export function documentTypeLabelKey(
  documentType: string | null | undefined
): (typeof DOCUMENT_TYPE_LABEL_KEY)[DocumentType] | null {
  return documentType && isDocumentType(documentType)
    ? DOCUMENT_TYPE_LABEL_KEY[documentType]
    : null;
}

/** The InOut evidence item each TelePortal document type can prove, when one fits. */
const INOUT_ITEM_OF_TELEPORTAL_TYPE: Readonly<Partial<Record<DocumentType, string>>> = {
  employment_contract: 'contract',
  promise_contract: 'contract',
  sponsor_letter: 'employer_declaration',
  birth_certificate: 'official_document',
  marriage_certificate: 'official_document',
  criminal_record: 'official_document',
  certified_translation: 'official_document',
  school_records: 'official_document',
  consulate_invitation_letter: 'official_document',
  term_of_responsibility: 'official_document',
  techvisa_endorsement: 'official_document',
};

/**
 * The InOut evidence item a document of `documentType` fits, among the items
 * the country `offered` — an InOut item is itself; a TelePortal type is the
 * item it can prove. Null when nothing fits, or the type is not known.
 */
export function inoutItemOfDocumentType(
  documentType: string | null | undefined,
  offered: readonly string[]
): string | null {
  if (!documentType) return null;
  if (offered.includes(documentType)) return documentType;
  const item = isDocumentType(documentType)
    ? INOUT_ITEM_OF_TELEPORTAL_TYPE[documentType]
    : undefined;
  return item && offered.includes(item) ? item : null;
}

/**
 * A country's evidence vocabulary in the product's own words (owner, 20 Oct
 * 2026): an item that is one of our document types (D2 — a boarding pass, a
 * passport stamp…) takes its professional label (`teleportal.docType.*`); the
 * rest keep the country's. The on-read translator must not name a fixed
 * taxonomy: it takes "Passport" in "Passport stamp" for the hero, and the
 * person read "Carimbo Passport".
 */
export function inoutEvidenceInProductWords<
  V extends { items: ReadonlyArray<{ id: string; label: string }> },
>(vocabulary: V, t: (key: string) => string): V {
  return {
    ...vocabulary,
    items: vocabulary.items.map((item) => {
      const key = documentTypeLabelKey(item.id);
      return key ? { ...item, label: t(key) } : item;
    }),
  };
}
